"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export interface DebouncedActionState<T> {
  data: T | null;
  pending: boolean;
}

/**
 * Debounce a server action and discard superseded responses.
 *
 * The sequence counter is the important half. Server actions cannot be
 * aborted, so a slow earlier request can still land after a newer one; without
 * the guard, results for "cam" could overwrite results for "campaign". Only the
 * response matching the latest issued sequence is applied.
 *
 * Same idiom as the media library's typeahead, extracted here so search and any
 * later caller share one implementation.
 *
 * Pass a stable `run` (useCallback): it is a dependency of `trigger`, so an
 * inline function makes `trigger` a new value every render. That is harmless
 * here (trigger is only called from event handlers) but wasteful.
 */
export function useDebouncedAction<TArg, TResult>(
  run: (arg: TArg) => Promise<TResult>,
  delayMs = 300,
): DebouncedActionState<TResult> & {
  trigger: (arg: TArg) => void;
  reset: () => void;
} {
  const [data, setData] = useState<TResult | null>(null);
  const [pending, setPending] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);

  // Clearing the timer on unmount stops a pending call from setting state on a
  // tree that is gone (closing the panel inside the debounce window).
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      seqRef.current += 1;
    };
  }, []);

  const trigger = useCallback(
    (arg: TArg) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setPending(true);
      timerRef.current = setTimeout(() => {
        const seq = ++seqRef.current;
        void run(arg)
          .then((result) => {
            if (seq !== seqRef.current) return;
            setData(result);
            setPending(false);
          })
          .catch(() => {
            if (seq !== seqRef.current) return;
            setPending(false);
          });
      }, delayMs);
    },
    [run, delayMs],
  );

  const reset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // Bump the sequence so an in-flight response cannot repopulate after a
    // reset (closing the panel, or clearing the query).
    seqRef.current += 1;
    setData(null);
    setPending(false);
  }, []);

  return { data, pending, trigger, reset };
}
